import { GetServerSideProps } from 'next';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Text,
  UnorderedList,
  Accordion,
  AccordionPanel,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
} from '@chakra-ui/react';
import NextHead from 'next/head';
import React from 'react';
import isMobileDevice from 'ismobilejs';
import NextLink from 'next/link';
import ReactMarkdown from 'react-markdown';
import { MdOpenInNew } from '../../components/Icons';
import { title } from '../../common/libs/metadata';
import { getPublicRuntimeConfig } from '../../common/config';
import FullWidthBox from '../../components/FullWidthBox';
import withAuth from '../../server/libs/withAuth';
import { NextPageWithLayout } from '../../common/types';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';

const GA_CATEGORY = 'docs_shosekika';

type PageContext = {
  isMobile: boolean;
};

const chapters: { title: string; notes?: string[] }[] = [
  {
    title: 'インタビュー',
    notes: ['山本大介 先生'],
  },
  {
    title: '一般的な書籍化までの流れ',
    notes: [
      '投稿',
      '出版社からの反応',
      '出版社との事前目合わせ',
      '企画メモ整理',
      '審査〜制作',
    ],
  },
  {
    title: 'FAQ',
    notes: [
      '印税/費用について',
      '著作権/画像/出典について',
      '共著/監修について',
    ],
  },
  {
    title: 'スライドを投稿してみよう',
  },
];

const TableOfContents = ({
  isMobile,
  chapters,
}: {
  isMobile: boolean;
  chapters: { title: string; notes?: string[] }[];
}) => {
  return (
    <List py={1}>
      {chapters.map(({ title, notes }, index) => {
        return (
          <ListItem
            key={title}
            py={0.5}
            fontSize="sm"
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="flex-start"
            onClick={() => {
              const id = `chapter-${index + 1}`;
              const target = document.getElementById(id);
              if (target) {
                window.scrollTo({
                  top: target.offsetTop - 70, // ヘッダーの高さ分調整
                  behavior: 'smooth',
                });
              }
            }}
            cursor="pointer"
          >
            <Flex
              color="primaryDark"
              fontWeight="bold"
              w={230}
              flexShrink={0}
              gap={1}
              fontSize="md"
            >
              <Text>{index + 1}.</Text>
              <Text textDecoration="underline">{title}</Text>
            </Flex>
            <Flex
              flexWrap="wrap"
              justifyContent="flex-start"
              ml={isMobile ? '1em' : 0}
              fontSize="md"
            >
              {notes ? (
                <List>
                  {notes.map((text) => (
                    <ListItem
                      key={text}
                      display={isMobile ? 'list-item' : 'inline-block'}
                      pr={1}
                    >
                      ・{text}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Text>{notes}</Text>
              )}
            </Flex>
          </ListItem>
        );
      })}
    </List>
  );
};

const ChapterHeader = ({
  title,
  number,
  isMobile,
}: {
  title: string;
  number: number;
  isMobile: boolean;
}) => {
  return (
    <Heading
      as="h3"
      id={`chapter-${number}`}
      fontSize={isMobile ? '2xl' : 'xl'}
      fontWeight="bold"
      py={1}
      px={4}
      mt={6}
      borderLeft="4px solid"
      borderColor="primaryMain"
    >
      {number}. {title}
    </Heading>
  );
};

const interviewItems: {
  question: string;
  answer: string;
}[] = [
  {
    question:
      '―― 先生がスライド作りを始められた原点を、まず教えていただけますか。',
    answer: `私は当時、転職が決まっておりましたので、新しい職場で新しい仕事を始めるにあたって、その準備のためにスライド投稿を始めました。前職から現在の職場に転職し、そこで新しいチームを作ることがまずは目標でした。その目標のためには「何らかの形でプレゼンテーションを行う」ことが必要だと考えました。
スライド投稿という形式は私には合っていましたし、当初から「いつかスライドを書籍化する」という目標を掲げていました。最初はすごく緊張しながら投稿したことを覚えています。私の場合は、新しい仕事を発展させるためにどうにかしないといけない、という切実なモチベーションがありましたので、スライド投稿については緊張しながらも、なんとか続けてきた経緯があります。`,
  },
  {
    question:
      '―― スライド作成において、大切にされている視点があればお聞かせください。',
    answer: `やや特殊かもしれませんが、通常のプレゼンテーションで使用するスライドは文字が少なく、要点のみで作られているはずです。しかし Antaa Slide に関しては、スライドのみを読んで内容を理解してもらう必要があります。ですから、私のスライドは“スライドのみで完結する”内容にするよう意識しています。視覚的に読んで理解可能であることを大事にしています。
また、スライド一つ一つに「テーマ」が必ずあるようにしています。特定のアイデア・テーマを体現するスライドコンテンツにするよう努力してきました。たとえば、末梢神経障害がテーマのスライドでは「benign neuropathy（よくある良性の神経障害）を理解して、末梢神経障害診療を捉えてみよう」という主題を込めています。もう一つ例を挙げるなら、認知症診療がテーマのスライドでは「自分自身の言葉で認知症外来を説明できるように、患者さん・家族への説明を明確に記す」といった主題があります。
このように、スライド作成においてはまず“そのスライドのコンセプトを決める”ところから始めています。`,
  },
  {
    question:
      '―― Antaa Slide への投稿後、手応えを感じられた瞬間はありましたか。',
    answer: `目に見えて閲覧数などが示されますので、実際に誰かに見てもらえたんだな、ということを知って嬉しかったです。また、違う施設に行ってスライドの存在を知っていてもらえたこともありがたく感じました。
最も嬉しかったのは、最初のスライド投稿の目的である「書籍を作る」チャンスを得られたタイミングでした。こうやって世界にアプローチを続ければ、いつかチャンスが得られるはず、と信じてやってきましたので、その願い通りにチャンスが得られたことは達成感がありました。もちろんこれはスタートラインに過ぎませんが、願えば願いは叶うのだという実感があり、とても嬉しかったです。`,
  },
  {
    question: '―― 書籍化の経緯はどのような形だったのでしょうか。',
    answer: `Antaa Slide への投稿を通じて、藤井達也先生に出版社を紹介していただけました。自分自身の力で出版社にアプローチする、ということは当時の私にはできませんでしたので、「いつか誰かに見つけてもらえたら」という都合のいい思いで日々過ごしていました。そして本当に“誰かに見つけてもらえた”ことは、本当に嬉しかったです。
積極的なのか受動的なのか、自分でも矛盾している態度だったと思います。ある程度は積極的なのですが、自信がなさすぎて消極的になってしまう、というアンビバレントなメンタリティでした。`,
  },
  {
    question: '―― 企画の軸はどのように固められましたか。',
    answer: `スライドもそうですが、書籍もコンセプトが大切です。何が書籍のオリジナリティで、どこにそのコンテンツの価値があるか、を十分に検討しました。
スライド一つ一つにテーマがある、と先述しましたが、スライド全体としてのテーマは「堅苦しく思われている脳神経内科を、わかりやすくみんなのものにする」というものです。ですから書籍はそのテーマを言語化して「みんなの脳神経内科」としました。“みんなのための脳神経内科”というジャンルを自分で作り、それを体現する、というのがテーマです。
そもそも私自身が抱えていた悩み・テーマでもありましたので、それをある程度解決し、誰かの役に立ちたいというモチベーションでコンテンツ作成を行っています。`,
  },
  {
    question: '―― 執筆で工夫された点は何でしょうか。',
    answer: `工夫は特にありません。とにかく“生みの苦しみ”につきました。業務をしながら執筆をするということがなかなか両立できず、まったく執筆が進まず、本当に辛かったです。`,
  },
  {
    question: '―― 出版社とのやり取りで、印象に残っている場面はありますか。',
    answer: `執筆が進まなくても、出版社の方が地道に待ってくださったことが大変ありがたく思えました。一方で「もっとコミットしてくれてもいいのに」と思ってしまう自分もいましたが…。
ただ、出版社の提案も受けつつ、自分がやりたい・実現したいことを具現化していく作業は、とても得難く素晴らしい経験でした。`,
  },
  {
    question: '―― 刊行後、現場からはどのような反響がありましたか。',
    answer: `執筆を考える方に、一応の先輩として伝えたいことがあります。それは「別に誰かは、思っているほど他人のことを気にしていない」という事実です。
本を書いて表現したら批判の対象になるのではないか、という考えは、人を行動から遠ざける理由の一つだと思います。私もめちゃくちゃびびっていました。しかし蓋をあければ、刊行後、肯定的な意見は少々、批判的な意見はほぼありませんでした。結局、誰もそこまで他人の本を読み込むわけではないのだ、と知りました。
書籍出版は悪いことは少なく、いいことは多いと思います。知名度が上がる、本を書いているというだけで立派な感じが演出できる、印税が入る、達成感がある、などです。
私は新しい職場で自分のチームを作る、ということが目標でした。そのために Antaa Slide を活用し、その中で書籍を作り、認知してもらいながらチームを作ってきました。いろいろなプレゼンテーションの方法がありますが、これは一つの手段です。
Antaa Slide の「あなたのPCに埋まっているプレゼンテーションを共有しませんか？」というコンセプトには、投稿を始める前から震えました。私たちは多く勉強する割に、それが自分の中だけに留まり、そのまま何にもならずに消えていく、という事実があります。これはとてももったいないことです。
かっこ悪くても、いびつでも、そこに想いや願いがあるなら、形にして残していけばいい。努力が誰かの役に立ち、その後の何かにつながるなら、そんなにいいことはありません。`,
  },
];

const publishingProcessItems: {
  title: string;
  text: string;
}[] = [
  {
    title: '投稿',
    text: '多くの場合、明確なテーマや切り口のスライドを公開するところから始まります。',
  },
  {
    title: '出版社からの反応',
    text: '編集者がAntaa Slideでスライドに出会い、DMやメールでご連絡をいただくことが多いです。注目されたポイントが共有されると、その後の打ち合わせが具体的になります。',
  },
  {
    title: '出版社との事前目合わせ',
    text: '初回の打ち合わせでは、想定読者・章立て・差別化の方向性、分量や刊行時期の目安をすり合わせることが一般的です。ここで大枠がそろうと、進行がスムーズになります。',
  },
  {
    title: '企画メモ整理',
    text: '骨子・目次・到達目標・サンプル図版を短い資料にまとめ、企画書ドラフトに落とし込みます。以降はこの資料を“共通の地図”として進めます。',
  },
  {
    title: '審査〜制作（法令/倫理/出典/画像/COI確認）',
    text: '症例の匿名化や画像の使用許諾、出典整理・表記統一を整えながら、原稿は初稿→改稿→校正へと進みます。準備が整い次第、入稿・発売へとつながります。',
  },
];

const faqItems: {
  question: string;
  answer: string; // markdown形式のテキスト
}[] = [
  {
    question: '印税/費用について',
    answer: `- **印税方式／買い取り方式**：商業出版は「印税方式」または「買い取り（原稿料一括）」が一般的です。印税方式は紙の場合「本体価格×部数×印税率」、電子は「実売（または出版社受取）×率」で計算されることが多いです。
- **印税率の目安**：一般書で8〜10％台、専門書では5〜10％台が目安です（契約とレーベルで変わります）。電子は紙より高めの率が設定されることがあります。
- **アドバンス（前払印税）**：契約時に前払いが発生する場合があります。売上から清算（リクープ）されるのが通常です。
- **費用負担の原則**：商業出版では編集・校閲・組版・印刷・流通・販促は出版社負担が基本です。第三者画像の使用料、特殊な図版制作費、取材・データ取得費などは、合意により著者側負担となる場合があります。
- **計算例（紙・参考）**：本体3,000円、初版3,000部、印税率8％ → 3,000×3,000×0.08＝**720,000円**。実売ベース契約の場合は「実売部数×本体価格×率」で試算します。
- **自費出版との違い**：自費出版は制作費を著者が負担します。本ページは商業出版（出版社主導）を前提としています。`,
  },
  {
    question: '著作権/画像/出典について',
    answer: `- **著作権と出版権**：著作権は原則として著者に帰属します。契約で出版社に**出版権（紙）や電子出版権**、翻訳・二次利用の扱いを設定します。期間・媒体・地域の範囲を契約書で明確にします。
- **スライド由来の図表**：著者が自ら作成した図表は著作物として扱われます。既存論文・教科書・Webからの転載・改変は必ず**権利確認**が必要です（引用要件を満たさない利用は転載許諾が必要）。
- **画像（臨床・施設・機器）**：患者さんの個人情報は**不可逆的匿名化**を徹底し、必要に応じて同意・施設内規程の確認を行います。機器写真・ブランドロゴ・施設外観等は肖像・商標の観点も確認します。
- **出典表記**：本文・図表ともに**出典の明示と一貫した書式**（例：Vancouver など）を採用します。DOI／PMIDを可能な限り付与し、図表内にも簡潔な帰属表記を入れます。
- **ライセンス整理**：Creative Commons 等の素材は**ライセンス条件（BY/NC/ND/SA 等）** を遵守します。再配布可否・改変可否・商用可否を事前に確認します。
- **記録の保全**：許諾メール、申請フォーム、契約書の写しは**証跡として保管**します（版元指定のフォルダ／台帳で一元管理）。`,
  },
  {
    question: '共著/監修について',
    answer: `- **役割定義**：
  - **著者**：章・項の執筆責任を負います。
  - **編者**：全体構成や用語統一、執筆要領の統括を担います。
  - **監修者**：学術的妥当性の最終確認を行います（執筆は限定的または無し）。
- **クレジットと配分**：共著の場合、**印税配分**は章数・分量・役割で事前合意し、契約書（付属明細）に明記します。監修は**定額謝金**または**印税の一定割合**のいずれかが一般的です。
- **執筆要領の統一**：用語表、略語表、図表テンプレート、引用ルール、COI様式を**最初に共有**し、スタイルのばらつきを防ぎます。
- **レビュー体制**：章責任者→編者（技術・構成）→監修（最終学術確認）→版元編集（表記・体裁）の**多段レビュー**を敷きます。指摘はチケット化してトレーサブルにします。
- **COIと責任範囲**：各執筆者・監修者のCOI申告を明記し、監修の範囲（全体／特定章）と責任線を契約時に固めます。
- **名前順の決定**：寄与度順・五十音順・役割別表記など、**表記方針を早期決定**します（表紙・奥付・販促物で統一）。`,
  },
];

const DoctorImage = () => (
  <Image
    src="/images/docs/shosekika_doctor_yamamoto.png"
    alt="山本大介先生の写真"
    w="full"
  />
);

const ShosekikaPage: NextPageWithLayout<PageContext> = ({
  isMobile,
}: PageContext) => {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}/docs/shosekika`;

  return (
    <>
      <NextHead>
        <title>{title('Antaa Slide 書籍化までの道のり')}</title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <Box>
        <Image
          src={
            isMobile
              ? '/images/docs/shosekika_head_mobile.png'
              : '/images/docs/shosekika_head_pc.png'
          }
          alt="スライド投稿が“本になる”きっかけに インタビュー「書籍化までの道のり」Antaa Slideから生まれた書籍化ストーリーを紹介します。"
          w="full"
        />
      </Box>
      <FullWidthBox bg="#EEF6ED" py={6}>
        <Container>
          <Box
            py={6}
            px={isMobile ? 2 : 8}
            backgroundColor="white"
            mx={{
              base: 0,
              lg: -2,
            }}
          >
            <Heading as="h3" fontSize="2xl">
              日々の臨床で培った“コツ”は、他の誰かにとってまだ言葉になっていない知識です。
            </Heading>
            <Box fontWeight="bold" py={2}>
              その一つをスライドに落とし込み、仲間の反応で磨き直すことで、経験は読むだけでなく“使える知識”に変わります。
              <br />
              積み重なった知見は章になり、やがて一冊の書籍へ。ここでは、その歩みを実例の会話を通じてご紹介します。
            </Box>
            <Box fontSize="xs" fontWeight="bold">
              目次
            </Box>
            <TableOfContents isMobile={isMobile} chapters={chapters} />
          </Box>
        </Container>
      </FullWidthBox>
      <Container py={6}>
        <ChapterHeader title="インタビュー" number={1} isMobile={isMobile} />
        <Box pb={10}>
          <Flex
            gap={{
              base: 4,
              lg: 8,
            }}
            p={isMobile ? 4 : 6}
            mt={4}
            mb={9}
            border="1px solid"
            borderColor="borderLight"
            borderRadius="lg"
            flexDirection={{
              base: 'column',
              md: 'row',
            }}
          >
            <Flex
              gap={4}
              w="full"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Box w="100px" flexShrink={0}>
                <DoctorImage />
              </Box>
              <Box flexDirection="column" alignItems="center">
                <Heading as="h4" fontSize="xl" mb={1}>
                  山本大介 先生
                </Heading>
                <Flex
                  fontSize="sm"
                  color="textGray"
                  whiteSpace="nowrap"
                  flexDirection={{
                    base: 'column',
                    lg: 'row',
                  }}
                  columnGap={1}
                >
                  <Box>順天堂大学医学部卒</Box>
                  <Box>湘南鎌倉総合病院 脳神経内科部長</Box>
                </Flex>
                <NextLink href={'/profile/xc56MRYQujaK'} passHref>
                  <Link
                    fontSize="sm"
                    fontWeight="bold"
                    onClick={() => {
                      sendAnalyticsEvent(GA_CATEGORY, 'click', 'profile');
                    }}
                  >
                    プロフィール詳細
                  </Link>
                </NextLink>
              </Box>
            </Flex>
            <Box>
              <Text whiteSpace="pre-wrap" fontSize="sm">
                河北総合病院で初期研修、聖隷浜松病院で脳神経内科専門研修を経て、2018年より現職。
                臨床で得た知見をスライドとして可視化・共有し、若手育成と診療の標準化に取り組む。
                日本神経学会専門医、日本内科学会総合内科専門医、日本脳卒中学会専門医。
                著書『みんなの脳神経内科
                Ver.2』（2024年）は、研修医・非専門医・プライマリケア医に向け、現場で機能する要点を簡潔にまとめた改訂版。
              </Text>
            </Box>
          </Flex>
          <Box>
            {interviewItems.map(({ question, answer }, index) => (
              <Box key={index}>
                <Heading
                  as="h4"
                  fontSize="md"
                  mb={2}
                  display="flex"
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  gap={2}
                >
                  <Image
                    src="/images/icon_antaa.svg"
                    alt="Antaaアイコン"
                    w={7}
                    display="inline"
                  />
                  <Box lineHeight={1.5}>{question}</Box>
                </Heading>
                <Flex bgColor="#fffbe6" p={4} gap={4} mb={9}>
                  <Box w="50px" flexShrink={0}>
                    <DoctorImage />
                  </Box>
                  <Text whiteSpace="pre-wrap">{answer}</Text>
                </Flex>
              </Box>
            ))}
          </Box>
          <Flex
            py={4}
            px={isMobile ? 2 : 6}
            bgColor="backgroundMedium"
            borderRadius="md"
            gap={4}
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'center' : 'flex-start'}
          >
            <Box w="110px" flexShrink={0}>
              <Image
                border="1px solid"
                src="/images/docs/shosekika_book_1.png"
                alt="「著書のご紹介 みんなの脳神経内科Ver.2」書影"
                w="full"
              />
            </Box>
            <Box position="relative">
              <Heading
                as="h4"
                fontSize="md"
                display="flex"
                flexDirection="column"
                gap={1}
                py={1}
              >
                <Text as="small" fontSize="xs">
                  著書のご紹介
                </Text>
                <Text fontSize="xl">みんなの脳神経内科Ver.2</Text>
                <Text>山本大介 先生 著</Text>
              </Heading>
              <Box whiteSpace="pre-wrap" fontSize="sm">
                レジデント，非専門医，プライマリケア医に最短距離で伝えたい！
                 脳梗塞，認知症，てんかん，パーキンソン病，しびれなど，プライマリケア領域や救急で遭遇する脳神経内科領域の主要疾患について，神経診察や画像診断のポイントなど，実際に現場で使える知識や診断テクニックを中心に，研修医や非専門医に向け著者の豊富な経験をもとに平易な言葉でわかりやすく“最短距離”で書かれた“みんなの”ための一冊．初版から新たに2章を追加し，全面修正を加えた改訂第2版．
              </Box>
              <Box position="absolute" top={0} right={0}>
                <Link
                  href="https://www.amazon.co.jp/dp/4498328736?ref=cm_sw_r_ffobk_cp_ud_dp_HQVG4RXHBJVPD3S277Q2&ref_=cm_sw_r_ffobk_cp_ud_dp_HQVG4RXHBJVPD3S277Q2&social_share=cm_sw_r_ffobk_cp_ud_dp_HQVG4RXHBJVPD3S277Q2&bestFormat=true"
                  onClick={() => {
                    sendAnalyticsEvent(GA_CATEGORY, 'click', 'book');
                  }}
                  isExternal
                >
                  <Flex alignItems="center">
                    <Text fontSize="xs" fontWeight="bold">
                      詳細を見る
                    </Text>
                    <Text fontSize="xl" fontWeight="bold">
                      <MdOpenInNew />
                    </Text>
                  </Flex>
                </Link>
              </Box>
            </Box>
          </Flex>
        </Box>
        <ChapterHeader
          title="一般的な書籍化までの流れ"
          number={2}
          isMobile={isMobile}
        />
        <Box py={6}>
          {publishingProcessItems.map(({ title, text }, index) => (
            <Box key={index} bg="white" pb={4}>
              <Heading
                as="h4"
                fontSize="lg"
                mb={1}
                display="flex"
                alignItems="flex-start"
                gap={2}
              >
                <Box>{index + 1}.</Box>
                <Box>{title}</Box>
              </Heading>
              <Text whiteSpace="pre-wrap">{text}</Text>
            </Box>
          ))}
        </Box>
        <ChapterHeader title="FAQ" number={3} isMobile={isMobile} />
        <Box pb={10}>
          <Box py={4}>
            本ページの内容は一般的な説明です。個別の条件や手続は出版社・レーベル・契約形態により異なりますので、最終的な取り扱いは担当編集者へのご確認と契約書の記載をご優先ください。
          </Box>
          <Accordion allowMultiple>
            {faqItems.map(({ question, answer }, index) => (
              <AccordionItem key={index}>
                <Heading as="h4">
                  <AccordionButton py={5} px={0}>
                    <Box
                      fontSize="lg"
                      fontWeight="bold"
                      flex="1"
                      textAlign="left"
                    >
                      Q. {question}
                    </Box>
                    <AccordionIcon color="primaryMain" />
                  </AccordionButton>
                </Heading>
                <AccordionPanel bgColor="backgroundMedium" p={2} mb={4}>
                  <ReactMarkdown
                    components={{
                      ul: ({ node, ...props }) => (
                        <UnorderedList ml={6} {...props} />
                      ),
                    }}
                  >
                    {answer}
                  </ReactMarkdown>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
        <ChapterHeader
          title="スライドを投稿してみよう"
          number={4}
          isMobile={isMobile}
        />
        <Box py={2}>
          誰もが最初は不安や迷いを抱えながら、試行錯誤の中でスライドを作ってきました。あなたの経験や工夫が詰まったその一枚が、これから学ぶ誰かにとって大きな気づきや支えになります。Antaa
          Slideは、医師同士が知識を分かち合い、ともに成長していく場所です。完璧である必要はありません。今のあなたが伝えられることを、ぜひスライドにして共有してみませんか？
        </Box>
        <Box textAlign="center" py={2}>
          <NextLink href={'/article/upload'} passHref>
            <Button
              colorScheme="brand"
              size="lg"
              onClick={() => {
                sendAnalyticsEvent(GA_CATEGORY, 'click', 'upload');
              }}
            >
              スライドを投稿してみる
            </Button>
          </NextLink>
        </Box>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    return {
      props: {
        isMobile,
      },
    };
  },
);

export default ShosekikaPage;
